import type { Metric } from "@sentry/core";
import { PMS_USER_ACTION_METRIC } from "src/app/support/sentry-metrics";

export const HTTP_CLIENT_DURATION_METRIC = "http_client_duration";

export const SENTRY_HTTP_METRICS_SAMPLE_RATE = 0.1;

export type HttpStatusClass = "0" | "2xx" | "3xx" | "4xx" | "5xx";

type FilterSentryMetricOptions = {
  random?: () => number;
};

const STATIC_ASSET_PATH_PATTERN =
  /\.(js|css|woff2?|ttf|eot|png|jpe?g|gif|webp|svg|ico|map)(\?|#|$)/i;
const STORAGE_URL_PATTERN = /storage\.snoots\.pet|snoots-storage/i;
const HEALTH_PING_PATH_PATTERN = /\/(health|ping)(\/|\?|#|$)/i;
const PLACES_PATH_PATTERN = /\/places(\/|\?|#|$)/i;

export function bucketStatusCode(statusCode: unknown): HttpStatusClass {
  const code: number =
    typeof statusCode === "number" ? statusCode : Number(statusCode);

  if (!Number.isFinite(code) || code === 0) {
    return "0";
  }

  if (code >= 200 && code < 300) {
    return "2xx";
  }

  if (code >= 300 && code < 400) {
    return "3xx";
  }

  if (code >= 400 && code < 500) {
    return "4xx";
  }

  if (code >= 500) {
    return "5xx";
  }

  return "0";
}

export function filterSentryMetric(
  metric: Metric,
  options?: FilterSentryMetricOptions
): Metric | null {
  const random: () => number = options?.random ?? Math.random;

  if (metric.name === PMS_USER_ACTION_METRIC) {
    return metric;
  }

  if (metric.name === HTTP_CLIENT_DURATION_METRIC) {
    if (random() > SENTRY_HTTP_METRICS_SAMPLE_RATE) {
      return null;
    }

    const attributes: Record<string, unknown> = {
      ...(metric.attributes ?? {})
    };
    const statusCode: unknown = attributes.status_code;

    delete attributes.queryParams;
    delete attributes.status_code;
    attributes.status_class = bucketStatusCode(statusCode);

    return {
      ...metric,
      attributes
    };
  }

  return null;
}

export function shouldSkipHttpClientDurationMetric(url: string): boolean {
  const path: string = url.split("?")[0]?.split("#")[0] ?? url;

  if (STATIC_ASSET_PATH_PATTERN.test(path)) {
    return true;
  }

  if (STORAGE_URL_PATTERN.test(url)) {
    return true;
  }

  if (HEALTH_PING_PATH_PATTERN.test(path)) {
    return true;
  }

  if (PLACES_PATH_PATTERN.test(path)) {
    return true;
  }

  return false;
}
